// =============================================================================
// ChauffeurStatementsPage — Slice 7d.7 Piece 2 + Piece 3
// =============================================================================
// The dedicated workflow page for preparing chauffeur pay. Lists everything
// in the pool, grouped by date, with type-aware actions:
//
//   Per-job driver: lines display in their booking-shape (ref, route, pax,
//                   vehicle), grouped by date. No consolidation.
//
//   Per-hour driver: lines also display in booking-shape until consolidated.
//                    Each day with fare lines gets a "Set hours" or
//                    "Consolidate day" button (label depends on count).
//                    Consolidated lines display in shift-shape (date, hours,
//                    amount), editable in place.
//
// In all cases:
//   - Expense lines render distinctly (passing through to PDF separately)
//   - Lines older than 14 days trigger a banner at the top of the page
//   - Empty pool shows a helpful empty state
//
// Piece 3 additions:
//   - Generate statement button (opens modal with date range picker)
//   - Statements history section listing past statements with status pills
//   - Each row links to /statements/:id (StatementDetailPage)
// =============================================================================

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    AlertTriangle,
    Calendar,
    Clock,
    Pencil,
    Trash2,
    Combine,
    FileText,
    ArrowLeft,
    ChevronRight,
    CheckCircle2,
    Ban,
    Send,
    Plus,
    Clock3,
} from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import {
    PageHeader,
    LoadingPage,
    ErrorPage,
    StatusPill,
} from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';

export function ChauffeurStatementsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const api = useApi();
    const { isOwner, isLoading: userLoading } = useCurrentUser();

    const poolQuery = useQuery({
        queryKey: ['statement-pool', id],
        queryFn: () => api.get(`/drivers/${id}/statement-pool`),
        enabled: isOwner && Boolean(id),
    });

    // 7d.7 Piece 3 — issued statements history. Independent query; rendered
    // below the pool. Refetches on void/mark-paid via invalidation from the
    // detail page mutations.
    const statementsQuery = useQuery({
        queryKey: ['statements', id],
        queryFn: () => api.get(`/drivers/${id}/statements`),
        enabled: isOwner && Boolean(id),
    });

    const [consolidating, setConsolidating] = useState(null); // { date, lines, defaultTotal }
    const [editingLine, setEditingLine] = useState(null);
    const [deletingLine, setDeletingLine] = useState(null);
    const [generating, setGenerating] = useState(false); // 7d.7 P3
    const [addingAdhoc, setAddingAdhoc] = useState(false); // 7d.7 P4

    if (userLoading) return <LoadingPage />;
    if (!isOwner) {
        return (
            <div className="p-6">
                <p className="text-sm text-ink-subtle">Owner access required.</p>
            </div>
        );
    }

    if (poolQuery.isLoading) return <LoadingPage />;
    if (poolQuery.isError) {
        return <ErrorPage message={poolQuery.error?.message} onRetry={poolQuery.refetch} />;
    }

    const { chauffeur, lines } = poolQuery.data;
    const isPerHour = chauffeur.pay_type === 'per_hour';

    // Group lines by effective_date. Order: oldest first (matches backend).
    const grouped = groupByDate(lines);
    const dates = Array.from(grouped.keys());

    // Stale banner — any line older than 14 days?
    const oldestLine = lines[0]; // backend orders oldest first
    const oldestDate = oldestLine?.effective_date;
    const oldestDays = oldestDate ? daysSince(oldestDate) : null;
    const isStale = oldestDays != null && oldestDays > 14;

    // Totals across the whole pool
    const totals = computeTotals(lines, chauffeur);

    return (
        <div>
            <PageHeader
                title={
                    <span className="inline-flex items-center gap-3 flex-wrap">
                        <span>Statements</span>
                        <span className="text-ink-subtle font-normal">·</span>
                                               <span className="font-normal">{chauffeur.first_name} {chauffeur.last_name}</span>
                        <span
                            className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${
                                isPerHour ? 'bg-ink text-paper' : 'bg-amber-100 text-amber-900'
                            }`}
                        >
                            {isPerHour ? 'Per hour' : 'Per job'}
                        </span>
                    </span>
                }
                subtitle={
                    <span>
                        {isPerHour ? 'Per hour' : 'Per job'}
                        {isPerHour && chauffeur.hourly_rate_pence != null && (
                            <> · £{(chauffeur.hourly_rate_pence / 100).toFixed(2)}/hr reference</>
                        )}
                        {chauffeur.vat_registered && <> · VAT registered ({chauffeur.vat_number || 'no number'})</>}
                    </span>
                }
                backTo={`/chauffeurs/${id}`}
                backLabel={`${chauffeur.first_name} ${chauffeur.last_name}`}
            />

            {isStale && (
                <div className="card mb-5 border-amber-200 bg-amber-50">
                    <div className="card-body flex items-center gap-3">
                        <AlertTriangle className="h-4 w-4 text-status-warning flex-shrink-0" />
                        <div className="text-sm text-status-warning">
                            Oldest line is <strong>{oldestDays} days old</strong>.
                            Consider whether you've missed adding a previous statement.
                        </div>
                    </div>
                </div>
            )}

            {/* Ad-hoc add — 7d.7 Piece 4 */}
            <div className="flex justify-end mb-3">
                <Button variant="secondary" size="sm" onClick={() => setAddingAdhoc(true)}>
                    <Plus className="h-3.5 w-3.5" />
                    Add line
                </Button>
            </div>

            {/* Pool totals strip */}
            <div className="card mb-5">
                <div className="card-body grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Stat label="Lines" value={lines.length} />
                    <Stat label="Days" value={dates.length} />
                    <Stat label="Subtotal" value={`£${totals.ex.toFixed(2)}`} suffix="ex VAT" />
                    <Stat label="Gross" value={`£${totals.gross.toFixed(2)}`} suffix="incl VAT" />
                </div>
            </div>

            {/* Empty state */}
            {lines.length === 0 ? (
                <div className="card">
                    <div className="card-body text-center py-12">
                        <FileText className="h-10 w-10 text-ink-subtle mx-auto mb-3" />
                        <p className="text-sm font-medium text-ink mb-1">No lines waiting</p>
                        <p className="text-xs text-ink-subtle max-w-md mx-auto">
                            Lock the chauffeur side of any booking from the booking detail page.
                            Lines will appear here ready to be added to a statement.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {dates.map((date) => {
                        const dayLines = grouped.get(date);
                        return (
                            <DayGroup
                                key={date}
                                date={date}
                                lines={dayLines}
                                isPerHour={isPerHour}
                                driverId={id}
                                chauffeur={chauffeur}
                                onConsolidate={(setup) => setConsolidating(setup)}
                                onEditLine={(line) => setEditingLine(line)}
                                onDeleteLine={(line) => setDeletingLine(line)}
                            />
                        );
                    })}
                </div>
            )}

            {/* Generate statement — live action (7d.7 Piece 3) */}
            {lines.length > 0 && (
                <div className="card mt-5">
                    <div className="card-body flex items-center justify-between gap-4">
                        <div>
                            <div className="text-sm font-medium text-ink">Ready to generate?</div>
                            <p className="text-xs text-ink-subtle mt-0.5">
                                Generate a statement from the {lines.length} line{lines.length === 1 ? '' : 's'} above.
                                Lines will be locked to the new statement.
                            </p>
                        </div>
                        <Button variant="primary" onClick={() => setGenerating(true)}>
                            <FileText className="h-4 w-4" />
                            Generate statement
                        </Button>
                    </div>
                </div>
            )}

            {/* Statements history — 7d.7 Piece 3 */}
            <StatementsHistorySection
                statementsQuery={statementsQuery}
                driverId={id}
            />

            {/* Modals */}
            {generating && (
                <GenerateStatementModal
                    driverId={id}
                    chauffeur={chauffeur}
                    lines={lines}
                    open
                    onClose={() => setGenerating(false)}
                />
            )}

            {addingAdhoc && (
                <AddAdhocLineModal
                    driverId={id}
                    chauffeur={chauffeur}
                    open
                    onClose={() => setAddingAdhoc(false)}
                    onDone={() => {
                        setAddingAdhoc(false);
                        poolQuery.refetch();
                    }}
                />
            )}

            {consolidating && (
                <ConsolidateDayModal
                    driverId={id}
                    chauffeur={chauffeur}
                    setup={consolidating}
                    open
                    onClose={() => setConsolidating(null)}
                    onDone={() => {
                        setConsolidating(null);
                        poolQuery.refetch();
                    }}
                />
            )}

            {editingLine && (
                <EditLineModal
                    line={editingLine}
                    open
                    onClose={() => setEditingLine(null)}
                    onDone={() => {
                        setEditingLine(null);
                        poolQuery.refetch();
                    }}
                />
            )}

            {deletingLine && (
                <DeleteLineModal
                    line={deletingLine}
                    open
                    onClose={() => setDeletingLine(null)}
                    onDone={() => {
                        setDeletingLine(null);
                        poolQuery.refetch();
                    }}
                />
            )}
        </div>
    );
}

// =============================================================================
// DayGroup — one date, with all lines that fall on it
// =============================================================================
function DayGroup({ date, lines, isPerHour, driverId, chauffeur, onConsolidate, onEditLine, onDeleteLine }) {
    // Within a day, separate fare / expense / deduction lines. Fares are
    // subject to consolidation (per-hour drivers); expenses pass through
    // gross; deductions subtract.
    const fareLines = lines.filter((l) => l.line_type === 'fare');
    const expenseLines = lines.filter((l) => l.line_type === 'expense');
    const deductionLines = lines.filter((l) => l.line_type === 'deduction');

    // Per-hour driver + has fare lines + at least one is booking-tied → can
    // consolidate. Once all fares are free-standing (already consolidated),
    // hide the button so we don't suggest a no-op.
    const consolidatableFares = fareLines.filter((l) => l.booking_id != null);
        const canConsolidate = consolidatableFares.length > 0;

    function handleConsolidateClick() {
        const defaultTotal = consolidatableFares.reduce(
            (sum, l) => sum + (Number(l.amount_ex_vat) || 0),
            0
        );
        onConsolidate({
            date,
            lines: consolidatableFares,
            defaultTotal,
        });
    }

    // Day total nets deductions out (matching backend statement math).
    const dayTotal = lines.reduce((sum, l) => {
        const amt = Number(l.amount_ex_vat) || 0;
        const sign = l.line_type === 'deduction' ? -1 : 1;
        return sum + sign * amt;
    }, 0);

    return (
        <div className="card">
            <div className="card-header flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-ink-subtle" />
                    <h3 className="text-sm font-semibold text-ink">{formatLongDate(date)}</h3>
                    <span className="text-xs text-ink-subtle">·</span>
                    <span className="text-xs text-ink-subtle">£{dayTotal.toFixed(2)}</span>
                </div>
                {canConsolidate && (
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleConsolidateClick}
                    >
                        <Combine className="h-3.5 w-3.5" />
                        {consolidatableFares.length === 1 ? 'Set hours' : 'Consolidate day'}
                    </Button>
                )}
            </div>

            <div className="divide-y divide-paper-border-soft">
                {fareLines.map((line) => (
                    <LineRow
                        key={line.id}
                        line={line}
                        chauffeur={chauffeur}
                        onEdit={() => onEditLine(line)}
                        onDelete={() => onDeleteLine(line)}
                    />
                ))}
{expenseLines.length > 0 && (
                    <>
                        <div className="px-6 py-2 bg-canvas/40">
                            <span className="text-xs uppercase tracking-wider font-medium text-ink-subtle">
                                Expenses
                            </span>
                        </div>
                        {expenseLines.map((line) => (
                            <LineRow
                                key={line.id}
                                line={line}
                                chauffeur={chauffeur}
                                onEdit={() => onEditLine(line)}
                                onDelete={() => onDeleteLine(line)}
                            />
                        ))}
                    </>
                )}

                {deductionLines.length > 0 && (
                    <>
                        <div className="px-6 py-2 bg-red-50/60">
                            <span className="text-xs uppercase tracking-wider font-medium text-red-700">
                                Deductions
                            </span>
                        </div>
                        {deductionLines.map((line) => (
                            <LineRow
                                key={line.id}
                                line={line}
                                chauffeur={chauffeur}
                                onEdit={() => onEditLine(line)}
                                onDelete={() => onDeleteLine(line)}
                            />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// LineRow — single line, shape depends on whether it's booking-tied
// =============================================================================
function LineRow({ line, chauffeur, onEdit, onDelete }) {
    const isFreeStanding = line.booking_id == null;
    const isExpense = line.line_type === 'expense';
    const isDeduction = line.line_type === 'deduction';

    return (
        <div className="px-6 py-3 flex items-start justify-between gap-4 hover:bg-paper-hover/30 transition-colors">
            <div className="min-w-0 flex-1">
                {isFreeStanding ? (
                    // Consolidated shift line OR free-standing expense
                    <div>
                        <div className="text-sm font-medium text-ink">
                            {line.label || (isExpense ? 'Expense' : 'Shift')}
                        </div>
                        <div className="text-xs text-ink-subtle mt-0.5 flex items-center gap-3 flex-wrap">
                            {line.hours != null && (
                                <span className="inline-flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {Number(line.hours)} hr
                                </span>
                            )}
                            {!isExpense && !line.hours && (
                                <span className="text-ink-faint italic">No hours set</span>
                            )}
                        </div>
                    </div>
                ) : (
                    // Booking-tied line — show booking metadata
                    <div>
                        <div className="text-sm font-medium text-ink flex items-center gap-2 flex-wrap">
                            <Link
                                to={`/bookings/${line.booking_id}`}
                                className="font-mono text-xs bg-canvas px-1.5 py-0.5 rounded hover:bg-paper-hover"
                            >
                                {line.ride_ref || 'ref?'}
                            </Link>
                            <span>{line.label || 'Fare'}</span>
                        </div>
                        <div className="text-xs text-ink-subtle mt-0.5 truncate">
                            {line.pickup_time && `${line.pickup_time.slice(0, 5)} · `}
                            {line.pickup_address}
                            {line.dropoff_address && ` → ${line.dropoff_address}`}
                        </div>
                        {(line.pax_name || line.vehicle_class_label) && (
                            <div className="text-xs text-ink-subtle mt-0.5">
                                {line.pax_name && `${line.pax_name}`}
                                {line.pax_name && line.vehicle_class_label && ' · '}
                                {line.vehicle_class_label}
                            </div>
                        )}
                    </div>
                )}
            </div>

<div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-right">
                    <div className={`text-sm font-medium ${isDeduction ? 'text-red-700' : 'text-ink'}`}>
                        {isDeduction ? '\u2212' : ''}£{Number(line.amount_ex_vat).toFixed(2)}
                    </div>
                    {Number(line.vat_rate) > 0 && (
                        <div className={`text-xs ${isDeduction ? 'text-red-700' : 'text-ink-subtle'}`}>
                            {isDeduction ? '\u2212' : '+'}£{(Number(line.amount_ex_vat) * Number(line.vat_rate)).toFixed(2)} VAT
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={onEdit}
                        className="p-1.5 rounded text-ink-subtle hover:text-ink hover:bg-paper-hover"
                        aria-label="Edit line"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={onDelete}
                        className="p-1.5 rounded text-ink-subtle hover:text-status-danger hover:bg-red-50"
                        aria-label="Delete line"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// ConsolidateDayModal — collapses booking-tied fare lines into one shift line
// =============================================================================
function ConsolidateDayModal({ driverId, chauffeur, setup, open, onClose, onDone }) {
    const api = useApi();
    const [hours, setHours] = useState('');
    const [totalEx, setTotalEx] = useState(setup.defaultTotal.toFixed(2));
    const [label, setLabel] = useState('');

    const isSingle = setup.lines.length === 1;
    const title = isSingle ? 'Set hours' : 'Consolidate day';

    const mutation = useMutation({
        mutationFn: () => api.post(`/drivers/${driverId}/consolidate-day`, {
            shift_date: setup.date,
            hours: hours.trim() === '' ? null : parseFloat(hours),
            total_amount: parseFloat(totalEx),
            label: label.trim() || '',
        }),
        onSuccess: () => {
            toast.success(isSingle ? 'Hours set' : `${setup.lines.length} lines consolidated`);
            onDone();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not consolidate';
            toast.error(msg);
        },
    });

    // Auto-fill hours from rate when both rate and total are present.
    // Convenience only — owner can override.
    function autofillHoursFromRate() {
        if (!chauffeur.hourly_rate_pence) return;
        const rate = chauffeur.hourly_rate_pence / 100;
        const total = parseFloat(totalEx);
        if (rate > 0 && Number.isFinite(total)) {
            const computed = (total / rate).toFixed(2);
            setHours(computed);
        }
    }

    const totalGross = (parseFloat(totalEx) || 0) * (chauffeur.vat_registered ? 1.20 : 1);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={title}
            description={
                <span>
                    {formatLongDate(setup.date)}
                    {!isSingle && ` · merging ${setup.lines.length} lines`}
                </span>
            }
            size="md"
        >
                       <div className="space-y-4">
                {chauffeur.pay_type !== 'per_hour' && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>
                            <strong>{chauffeur.first_name} is a per-job chauffeur.</strong> Only continue if this day
                            was paid by the hour — the job{isSingle ? '' : 's'} will show as one hourly line on the
                            statement, without itinerary.
                        </span>
                    </div>
                )}
                {!isSingle && (
                    <div className="bg-canvas/60 rounded-md p-3 text-xs text-ink-subtle space-y-1">
                        <div className="font-medium text-ink mb-1.5">Lines being merged:</div>
                        {setup.lines.map((l) => (
                            <div key={l.id} className="flex justify-between">
                                <span>{l.ride_ref || 'ref?'} · {l.label || 'Fare'}</span>
                                <span>£{Number(l.amount_ex_vat).toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Hours <span className="text-ink-faint normal-case">(optional)</span>
                        </label>
                        <input
                            type="text"
                            inputMode="decimal"
                            value={hours}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                                    setHours(v);
                                }
                            }}
                            placeholder="e.g. 8"
                            className="w-full px-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                        />
                        {chauffeur.hourly_rate_pence && (
                            <button
                                type="button"
                                onClick={autofillHoursFromRate}
                                className="text-xs text-ink-subtle hover:text-ink underline decoration-dotted mt-1"
                            >
                                Auto from £{(chauffeur.hourly_rate_pence / 100).toFixed(2)}/hr
                            </button>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Total (ex VAT)
                        </label>
                        <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-ink-subtle pointer-events-none">£</span>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={totalEx}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                                        setTotalEx(v);
                                    }
                                }}
                                className="w-full pl-6 pr-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                            />
                        </div>
                        {chauffeur.vat_registered && (
                            <div className="text-xs text-ink-subtle mt-1">
                                Gross: £{totalGross.toFixed(2)}
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                        Label <span className="text-ink-faint normal-case">(optional)</span>
                    </label>
                    <input
                        type="text"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="e.g. Half day, Standby — leave blank for plain shift"
                        className="w-full px-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                    />
                </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    variant="primary"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                    disabled={!totalEx || parseFloat(totalEx) <= 0}
                >
                    {isSingle ? 'Set hours' : `Consolidate ${setup.lines.length} lines`}
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// EditLineModal — inline edit for label, amount, hours (any line type)
// =============================================================================
function EditLineModal({ line, open, onClose, onDone }) {
    const api = useApi();
    const [label, setLabel] = useState(line.label || '');
    const [amountEx, setAmountEx] = useState(Number(line.amount_ex_vat).toFixed(2));
    const [hours, setHours] = useState(line.hours != null ? String(line.hours) : '');

    const mutation = useMutation({
        mutationFn: () => {
            const payload = {};
            if (label !== (line.label || '')) payload.label = label || null;
            if (amountEx !== Number(line.amount_ex_vat).toFixed(2)) {
                payload.amount_ex_vat = parseFloat(amountEx);
            }
            const newHours = hours.trim() === '' ? null : parseFloat(hours);
            const oldHours = line.hours != null ? Number(line.hours) : null;
            if (newHours !== oldHours) payload.hours = newHours;

            // Lines are accessed via /bookings/:id/billing-lines/:lineId — but
            // free-standing lines have no booking_id. The PATCH route checks
            // booking_id matches; we need a different access path for
            // free-standing lines. For now, route through the booking if present;
            // free-standing line edits will need a separate endpoint added in
            // a follow-up.
            //
            // TODO Piece 2 followup: add PATCH /billing-lines/:id (no booking
            // scope) for free-standing lines, or accept any booking_id with
            // a chauffeur_id check.
            if (line.booking_id) {
    return api.patch(
        `/bookings/${line.booking_id}/billing-lines/${line.id}`,
        payload
    );
}
return api.patch(`/billing-lines/${line.id}`, payload);
        },
        onSuccess: () => {
            toast.success('Line updated');
            onDone();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not update line';
            toast.error(msg);
        },
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Edit line"
            description={line.ride_ref ? `Ref ${line.ride_ref}` : 'Free-standing line'}
            size="md"
        >
            <div className="space-y-4">
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                        Label
                    </label>
                    <input
                        type="text"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                    />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Amount (ex VAT)
                        </label>
                        <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-ink-subtle pointer-events-none">£</span>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={amountEx}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                                        setAmountEx(v);
                                    }
                                }}
                                className="w-full pl-6 pr-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                            />
                        </div>
                    </div>

                    {line.booking_id == null && (
                        <div>
                            <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                                Hours
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={hours}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                                        setHours(v);
                                    }
                                }}
                                placeholder="e.g. 8"
                                className="w-full px-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    variant="primary"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                >
                    Save
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// DeleteLineModal — confirmation + hard delete
// =============================================================================
function DeleteLineModal({ line, open, onClose, onDone }) {
    const api = useApi();

    const mutation = useMutation({
        mutationFn: () => {
            if (line.booking_id) {
    return api.delete(
        `/bookings/${line.booking_id}/billing-lines/${line.id}?override_lock=true`
    );
}
return api.delete(`/billing-lines/${line.id}`);
        },
        onSuccess: () => {
            toast.success('Line deleted');
            onDone();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not delete line';
            toast.error(msg);
        },
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Delete this line?"
            description="This removes the line from the pool. The original booking is not affected."
            size="md"
        >
            <div className="bg-canvas/60 rounded-md p-3 text-sm">
                <div className="font-medium text-ink">{line.label || 'Untitled'}</div>
                <div className="text-xs text-ink-subtle mt-1">
                    £{Number(line.amount_ex_vat).toFixed(2)} ex VAT
                </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    variant="danger"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                >
                    <Trash2 className="h-4 w-4" />
                    Delete
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// GenerateStatementModal — date range picker + generate (7d.7 Piece 3)
// =============================================================================
// Defaults the period to last Mon-Sun (the most common weekly statement
// pattern for chauffeur pay). Owner picks any range; backend filters lines
// to those whose effective_date falls within the window.
//
// Posts to POST /drivers/:id/statements with { period_start, period_end }.
// Backend returns the new statement; we navigate to its detail page on success.
// =============================================================================
function GenerateStatementModal({ driverId, chauffeur, lines, open, onClose }) {
    const api = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { mondayOfLastWeek, sundayOfLastWeek } = computeLastFullWeek();
    const [periodStart, setPeriodStart] = useState(mondayOfLastWeek);
    const [periodEnd, setPeriodEnd] = useState(sundayOfLastWeek);

    // Lines that fall in the chosen window — count helps user verify range
    const linesInRange = lines.filter((l) => {
                if (!l.effective_date) return false;
        const d = String(l.effective_date).slice(0, 10);   // compare YYYY-MM-DD only
        return d >= periodStart && d <= periodEnd;
    });

    const generateMutation = useMutation({
        mutationFn: () =>
            api.post(`/drivers/${driverId}/statements`, {
                period_start: periodStart,
                period_end: periodEnd,
            }),
        onSuccess: (result) => {
            // Backend returns { statement, line_count, gross_total_pence }
            const statement = result?.statement || result;
            toast.success(`Statement #${statement.statement_number} generated`);
            queryClient.invalidateQueries({ queryKey: ['statement-pool', driverId] });
            queryClient.invalidateQueries({ queryKey: ['statements', driverId] });
            onClose();
            navigate(`/statements/${statement.id}`);
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not generate statement';
            toast.error(msg);
        },
    });

    const valid = periodStart && periodEnd
        && periodStart <= periodEnd
        && linesInRange.length > 0
        && !generateMutation.isPending;

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Generate statement"
            description={`Statement for ${chauffeur.first_name} ${chauffeur.last_name}. The number is assigned when you confirm.`}
            size="md"
        >
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Period start
                        </label>
                        <input
                            type="date"
                            value={periodStart}
                            onChange={(e) => setPeriodStart(e.target.value)}
                            disabled={generateMutation.isPending}
                            className="w-full px-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Period end
                        </label>
                        <input
                            type="date"
                            value={periodEnd}
                            onChange={(e) => setPeriodEnd(e.target.value)}
                            disabled={generateMutation.isPending}
                            className="w-full px-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                        />
                    </div>
                </div>

                <div className="p-3 rounded-md bg-paper-hover/40 border border-paper-border-soft">
                    <div className="text-xs text-ink-subtle mb-1">
                        Lines that will be added to this statement:
                    </div>
                    <div className="text-2xl font-semibold text-ink">
                        {linesInRange.length}
                        <span className="text-xs text-ink-subtle font-normal ml-2">
                            of {lines.length} in pool
                        </span>
                    </div>
                </div>

                {periodStart && periodEnd && periodStart > periodEnd && (
                    <p className="text-xs text-status-danger">
                        Period start must be before period end.
                    </p>
                )}
                {linesInRange.length === 0 && (
                    <p className="text-xs text-status-warning">
                        No lines fall within this date range.
                    </p>
                )}
                {linesInRange.length > 0 && linesInRange.length < lines.length && (
                    <p className="text-xs text-status-warning">
                        {lines.length - linesInRange.length} line{lines.length - linesInRange.length === 1 ? '' : 's'} fall
                        outside this range and will stay in the pool.
                    </p>
                )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={generateMutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => generateMutation.mutate()}
                    disabled={!valid}
                    loading={generateMutation.isPending}
                >
                    <FileText className="h-4 w-4" />
                    Generate statement
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// AddAdhocLineModal — create a free-standing chauffeur line directly (7d.7 P4)
// =============================================================================
// Lands the line in the statement pool without going through a booking.
// Use cases: standby days, expense reimbursements, bonuses, manual
// adjustments, recovering from accidental deletion of a consolidated line.
//
// Form behaviour:
//   - Type toggle: Fare (default) / Expense 
//   - VAT auto: 20% on fare for VAT-registered chauffeurs, 0 otherwise.
//     Expenses always 0% (passed through gross).
//   - Hours: only shown for per-hour chauffeurs + Fare type.
//   - Date: defaults to today.
//
// POSTs to /drivers/:id/billing-lines.
// =============================================================================
function AddAdhocLineModal({ driverId, chauffeur, open, onClose, onDone }) {
    const api = useApi();
    const isPerHour = chauffeur.pay_type === 'per_hour';

    const [lineType, setLineType] = useState('fare');
    const [label, setLabel] = useState('');
    const [amountEx, setAmountEx] = useState('');
    const [hours, setHours] = useState('');
    const [shiftDate, setShiftDate] = useState(toIsoDate(new Date()));
    const [vatRateState, setVatRateState] = useState(chauffeur.vat_registered ? 0.20 : 0);

    // VAT defaults follow line_type, but the user can override on any line.
    // Reset to a sensible default when line_type toggles, then user can
    // override via the dropdown.
    const vatRate = vatRateState;

        const showHours = lineType === 'fare';

    const mutation = useMutation({
        mutationFn: () => api.post(`/drivers/${driverId}/billing-lines`, {
            line_type: lineType,
            label: label.trim(),
            amount_ex_vat: parseFloat(amountEx),
            vat_rate: vatRate,
            shift_date: shiftDate,
            hours: showHours && hours.trim() !== '' ? parseFloat(hours) : null,
        }),
        onSuccess: () => {
            toast.success('Line added to pool');
            onDone();
        },
        onError: (err) => {
            const msg = err?.response?.error || err?.message || 'Could not add line';
            toast.error(msg);
        },
    });

    const valid = label.trim().length > 0
        && amountEx !== ''
        && parseFloat(amountEx) > 0
        && shiftDate
        && !mutation.isPending;

    const grossPreview = (parseFloat(amountEx) || 0) * (1 + vatRate);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Add line to pool"
            description={`Free-standing line for ${chauffeur.first_name} ${chauffeur.last_name}.`}
            size="md"
        >
            <div className="space-y-4">
{/* Type toggle */}
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                        Type
                    </label>
                    <div className="inline-flex rounded-md border border-paper-border overflow-hidden">
                        <button
                            type="button"
                            onClick={() => {
                                setLineType('fare');
                                setVatRateState(chauffeur.vat_registered ? 0.20 : 0);
                            }}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                                lineType === 'fare'
                                    ? 'bg-ink text-paper'
                                    : 'bg-paper text-ink hover:bg-paper-hover'
                            }`}
                        >
                            Fare
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setLineType('expense');
                                setHours('');
                                setVatRateState(0);
                            }}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-paper-border ${
                                lineType === 'expense'
                                    ? 'bg-amber-100 text-amber-900'
                                    : 'bg-paper text-ink hover:bg-paper-hover'
                            }`}
                        >
                            Expense
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setLineType('deduction');
                                setHours('');
                                setVatRateState(0);
                            }}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-paper-border ${
                                lineType === 'deduction'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-paper text-ink hover:bg-paper-hover'
                            }`}
                        >
                            Deduction
                        </button>
                    </div>
                </div>

                {/* Label */}
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                        Label
                    </label>
                    <input
                        type="text"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder={lineType === 'fare' ? 'e.g. Standby day, Holiday bonus' : 'e.g. Car park, Fuel reimbursement'}
                        className="w-full px-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                    />
                </div>

            {/* Amount + Date row */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Amount (ex VAT)
                        </label>
                        <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-ink-subtle pointer-events-none">£</span>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={amountEx}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                                        setAmountEx(v);
                                    }
                                }}
                                placeholder="0.00"
                                className="w-full pl-6 pr-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                            />
                        </div>
                        {/* VAT selector — editable for all line types, defaults
                            sensibly when type toggles. Mirrors the booking-side
                            billing card pattern. */}
                        <div className="mt-2 flex items-center gap-2">
                            <label className="text-xs text-ink-subtle">VAT</label>
                            <select
                                value={vatRateState}
                                onChange={(e) => setVatRateState(parseFloat(e.target.value))}
                                className="text-xs border border-paper-border rounded px-1.5 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                            >
                                <option value={0}>0%</option>
                                <option value={0.2}>20%</option>
                            </select>
                            {parseFloat(amountEx) > 0 && (
                                <span className={`text-xs ${lineType === 'deduction' ? 'text-red-700' : 'text-ink-subtle'}`}>
                                    {lineType === 'deduction' ? '−' : ''}£{grossPreview.toFixed(2)}{vatRate > 0 ? ' incl. VAT' : ''}
                                </span>
                            )}
                        </div>
                        {lineType === 'deduction' && (
                            <div className="text-xs text-red-700 mt-1">
                                Subtracts from chauffeur statement total.
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Date
                        </label>
                        <input
                            type="date"
                            value={shiftDate}
                            onChange={(e) => setShiftDate(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                        />
                    </div>
                </div>

                {/* Hours — per-hour + fare only */}
                {showHours && (
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Hours <span className="text-ink-faint normal-case">(optional)</span>
                        </label>
                        <input
                            type="text"
                            inputMode="decimal"
                            value={hours}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                                    setHours(v);
                                }
                            }}
                            placeholder="e.g. 8"
                            className="w-full px-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                        />
                    </div>
                )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => mutation.mutate()}
                    disabled={!valid}
                    loading={mutation.isPending}
                >
                    <Plus className="h-4 w-4" />
                    Add line
                </Button>
            </div>
        </Modal>
    );
}

function computeLastFullWeek() {
    // Returns { mondayOfLastWeek, sundayOfLastWeek } as YYYY-MM-DD.
    // Last full week = Mon-Sun preceding the most recent Sunday.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = today.getDay(); // 0 = Sun, ..., 6 = Sat
    // Days back to last Sunday: if today's Sunday, that was 7 days ago (so we
    // grab the Mon-Sun before it). Otherwise it's just "day" days ago.
    const daysToLastSunday = day === 0 ? 7 : day;
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - daysToLastSunday);
    const lastMonday = new Date(lastSunday);
    lastMonday.setDate(lastSunday.getDate() - 6);
    return {
        mondayOfLastWeek: toIsoDate(lastMonday),
        sundayOfLastWeek: toIsoDate(lastSunday),
    };
}

function toIsoDate(d) {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
}

// =============================================================================
// StatementsHistorySection — list of issued statements (7d.7 Piece 3)
// =============================================================================
function StatementsHistorySection({ statementsQuery, driverId }) {
    if (statementsQuery.isLoading) {
        return (
            <div className="card mt-5">
                <div className="card-body">
                    <p className="text-sm text-ink-subtle">Loading statements history…</p>
                </div>
            </div>
        );
    }

    if (statementsQuery.isError) {
        return (
            <div className="card mt-5 border-amber-200 bg-amber-50">
                <div className="card-body">
                    <p className="text-sm text-status-warning">
                        Could not load statements history: {statementsQuery.error?.message || 'Unknown error'}
                    </p>
                </div>
            </div>
        );
    }

    const statements = statementsQuery.data?.statements || [];

    return (
        <div className="card mt-5">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Statements history</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    Past statements for this chauffeur, newest first.
                </p>
            </div>
            {statements.length === 0 ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle">No statements issued yet.</p>
                </div>
            ) : (
                <div className="divide-y divide-paper-border-soft">
                    {statements.map((s) => (
                        <Link
                            key={s.id}
                            to={`/statements/${s.id}`}
                            className="block px-6 py-4 hover:bg-paper-hover/50 transition-colors"
                        >
                            <div className="flex items-center gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold text-ink">
                                            #{s.statement_number}
                                        </span>
                                        <StatementStatusPill status={s.status} />
                                    </div>
                                    <div className="text-xs text-ink-subtle mt-1">
                                        {formatDateShort(s.period_start)} – {formatDateShort(s.period_end)}
                                        {' · '}
                                        {s.line_count} line{s.line_count === 1 ? '' : 's'}
                                        {' · '}
                                        Issued {formatDateShort(s.issued_at)}
                                        {s.emailed_at && ' · Emailed'}
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <div className="text-sm font-semibold text-ink">
                                        £{((s.grand_total_pence || 0) / 100).toFixed(2)}
                                    </div>
                                    <div className="text-xs text-ink-subtle">grand total</div>
                                </div>
                                <ChevronRight className="h-4 w-4 text-ink-subtle flex-shrink-0" />
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
function StatementStatusPill({ status }) {
    const config = {
        pending: { tone: 'warning', label: 'Pending', icon: Clock3 },
        sent:    { tone: 'neutral', label: 'Sent',    icon: Send },
        paid:    { tone: 'success', label: 'Paid',    icon: CheckCircle2 },
        voided:  { tone: 'warning', label: 'Voided',  icon: Ban },
    }[status] || { tone: 'neutral', label: status };
    const Icon = config.icon;
    return (
        <StatusPill status={config.tone}>
            {Icon && <Icon className="h-3 w-3" />}
            {config.label}
        </StatusPill>
    );
}

function formatDateShort(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
}

// =============================================================================
// Helpers
// =============================================================================
function Stat({ label, value, suffix }) {
    return (
        <div>
            <div className="text-xs text-ink-subtle uppercase tracking-wider font-medium">{label}</div>
            <div className="text-lg font-semibold text-ink mt-0.5">
                {value}
                {suffix && <span className="text-xs text-ink-subtle font-normal ml-1">{suffix}</span>}
            </div>
        </div>
    );
}

function groupByDate(lines) {
    const map = new Map();
    for (const line of lines) {
        // effective_date arrives as full ISO timestamp from backend
        // (e.g. "2026-05-03T00:00:00.000Z"). Truncate to YYYY-MM-DD so
        // downstream code (especially consolidate-day backend which
        // requires YYYY-MM-DD format) sees a clean date string.
        const rawDate = line.effective_date;
        const date = rawDate ? rawDate.slice(0, 10) : 'unscheduled';
        if (!map.has(date)) map.set(date, []);
        map.get(date).push(line);
    }
    return map;
}

function computeTotals(lines, chauffeur) {
    // Three-way split: fares/expenses contribute positively, deductions
    // subtract. Mirrors the backend statement generation in
    // chauffeurStatements.js so the pool stats match what the resulting
    // statement will show.
    let ex = 0;
    let vat = 0;
    for (const l of lines) {
        const amt = Number(l.amount_ex_vat) || 0;
        const rate = Number(l.vat_rate) || 0;
        const sign = l.line_type === 'deduction' ? -1 : 1;
        ex += sign * amt;
        vat += sign * amt * rate;
    }
    return { ex, vat, gross: ex + vat };
}

function daysSince(dateStr) {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return Math.floor((today - d) / (1000 * 60 * 60 * 24));
}

function formatLongDate(dateStr) {
    if (!dateStr || dateStr === 'unscheduled') return 'Unscheduled';
    return new Date(dateStr).toLocaleDateString('en-GB', {
        weekday: 'long', day: '2-digit', month: 'short', year: 'numeric',
    });
}
